import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Crosshair } from 'lucide-react';
import toast from 'react-hot-toast';
import { Location } from '../types';
import { MapProps, MapRefs, MapState, HoverCard } from './Map/types';
import { BELARUS_BOUNDS } from './Map/constants';
import { 
  clearMarkers, 
  updateUserLocationMarker, 
  updateMarkerPosition, 
  addLocationMarkers 
} from './Map/mapUtils';
import { handleMyLocationClick } from './Map/geolocation';
import { handleSearch, handleCoordinatesSubmit } from './Map/search';
import { SearchControls } from './Map/SearchControls';
import { MapHoverCard } from './Map/HoverCard';
import { MobileBottomSheet } from './Map/MobileBottomSheet';

export const Map: React.FC<MapProps> = ({ 
  center, 
  onClick, 
  withSearch, 
  locations = [],
  onLocationClick,
  height = '400px',
  showUserLocation = false
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<ymaps.Map | null>(null);
  const placemarkRef = useRef<ymaps.Placemark | null>(null);
  const userPlacemarkRef = useRef<ymaps.Placemark | null>(null);
  const markersRef = useRef<ymaps.Placemark[]>([]);
  const isInitializedRef = useRef(false);

  const refs: MapRefs = {
    mapRef,
    mapInstanceRef,
    placemarkRef,
    userPlacemarkRef,
    markersRef
  };

  const [state, setState] = useState<MapState>({
    searchQuery: '',
    coordinates: `${center[0]}, ${center[1]}`,
    isMapReady: false,
    userLocation: null,
    hoverCard: null,
    mobileBottomSheet: null,
    isSheetAnimating: false
  });

  const updateState = useCallback((updates: Partial<MapState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Определяем мобильное устройство
  const isMobile = useMemo(() => window.innerWidth < 768, []);

  // Мемоизируем обработчики для предотвращения лишних ререндеров
  const showHoverCard = useCallback((location: Location, marker: ymaps.Placemark) => {
    // Отключаем hover карточки на мобильных устройствах
    if (isMobile || !mapInstanceRef.current || !mapRef.current) return;

    try {
      const pixelPosition = mapInstanceRef.current.converter.globalToPage(
        [location.coordinates.lat, location.coordinates.lng]
      );

      if (!pixelPosition) return;

      const mapContainer = mapRef.current;
      const rect = mapContainer.getBoundingClientRect();
      const x = pixelPosition[0] + rect.left;
      const y = pixelPosition[1] + rect.top;

      updateState({
        hoverCard: {
          location,
          x,
          y: y - 10
        }
      });
    } catch (error) {
      console.error('Error showing hover card:', error);
    }
  }, [updateState, isMobile]);

  const hideHoverCard = useCallback(() => {
    updateState({ hoverCard: null });
  }, [updateState]);

  const showMobileBottomSheet = useCallback((location: Location) => {
    updateState({
      isSheetAnimating: true,
      mobileBottomSheet: location
    });
    
    // Легкая вибрация на мобильных
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    toast.success(`Выбрана: ${location.name}`, {
      duration: 2000,
      icon: '📍',
    });
  }, [updateState]);

  const hideMobileBottomSheet = useCallback(() => {
    updateState({ isSheetAnimating: false });
    setTimeout(() => {
      updateState({ mobileBottomSheet: null });
    }, 300);
  }, [updateState]);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || mapInstanceRef.current || isInitializedRef.current) return;

    try {
      isInitializedRef.current = true;
      
      // Мобильные оптимизации для Яндекс Карт
      const mobileOptimizations = isMobile ? {
        // Отключаем некоторые функции на мобильных для производительности
        suppressMapOpenBlock: true,
        yandexMapDisablePoiInteractivity: true,
        // Уменьшаем качество рендеринга на мобильных
        pixelRatio: window.devicePixelRatio > 1 ? 1.5 : 1,
        // Отключаем сглаживание на мобильных
        smooth: false,
        // Ограничиваем анимации
        maxAnimationZoomDifference: 3,
        // Быстрые переходы
        autoFitToViewport: 'ifNull'
      } : {
        suppressMapOpenBlock: true,
        yandexMapDisablePoiInteractivity: true,
        avoidFractionalZoom: false,
        maxAnimationZoomDifference: 5,
        autoFitToViewport: 'always'
      };

      const map = new window.ymaps.Map(mapRef.current, {
        center: center,
        zoom: onClick ? 13 : 14,
        controls: isMobile ? ['zoomControl'] : ['zoomControl', 'typeSelector'],
        restrictMapArea: BELARUS_BOUNDS
      }, mobileOptimizations);

      mapInstanceRef.current = map;
      
      // Мобильные оптимизации событий
      if (isMobile) {
        // Отключаем некоторые события на мобильных для производительности
        map.events.remove('wheel');
        map.events.remove('dblclick');
        
        // Оптимизируем touch события
        map.options.set('touchZoomByTwoFingers', true);
        map.options.set('suppressMapOpenBlock', true);
      }
      
      if (onClick) {
        updateMarkerPosition(center, refs, onClick);
        
        map.events.add('click', (e: { get: (arg0: string) => [number, number]; }) => {
          const coords = e.get('coords');
          onClick(coords[0], coords[1]);
          updateState({ coordinates: `${coords[0]}, ${coords[1]}` });
          updateMarkerPosition(coords, refs, onClick);
        });
      } else if (locations.length === 0) {
        if (showUserLocation) {
          handleMyLocationClick(
            refs,
            (location) => updateState({ userLocation: location }),
            onClick
          );
        } else {
          updateMarkerPosition(center, refs, onClick);
        }
      } else {
        addLocationMarkers(
          locations,
          refs,
          showUserLocation,
          state.userLocation,
          onLocationClick,
          showHoverCard,
          hideHoverCard,
          showMobileBottomSheet
        );
      }

      updateState({ isMapReady: true });
    } catch (error) {
      console.error('Error initializing map:', error);
      toast.error('Не атрымалася ініцыялізаваць карту');
      isInitializedRef.current = false;
    }
  }, [center, onClick, locations, showUserLocation, state.userLocation, onLocationClick, showHoverCard, hideHoverCard, showMobileBottomSheet, updateState, isMobile]);

  // Инициализация карты
  useEffect(() => {
    if (window.ymaps && !isInitializedRef.current) {
      window.ymaps.ready(() => {
        initializeMap();
      });
    }

    return () => {
      if (mapInstanceRef.current) {
        clearMarkers(refs);
        if (userPlacemarkRef.current) {
          mapInstanceRef.current.geoObjects.remove(userPlacemarkRef.current);
          userPlacemarkRef.current = null;
        }
        if (placemarkRef.current) {
          mapInstanceRef.current.geoObjects.remove(placemarkRef.current);
          placemarkRef.current = null;
        }
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        isInitializedRef.current = false;
        updateState({ isMapReady: false });
      }
    };
  }, []);

  // Мемоизируем проверку изменения центра
  const centerChanged = useMemo(() => {
    if (!mapInstanceRef.current || !state.isMapReady) return false;
    const [curLat, curLng] = mapInstanceRef.current.getCenter();
    return Math.abs(curLat - center[0]) > 0.001 || Math.abs(curLng - center[1]) > 0.001;
  }, [center, state.isMapReady]);

  // Обновление центра карты
  useEffect(() => {
    if (centerChanged) {
      mapInstanceRef.current!.setCenter(center);
      updateState({ coordinates: `${center[0]}, ${center[1]}` });
      if (onClick) {
        updateMarkerPosition(center, refs, onClick);
      }
    }
  }, [centerChanged, center, onClick, updateState]);

  // Обновление локаций с дебаунсом (увеличенный для мобильных)
  useEffect(() => {
    if (!mapInstanceRef.current || !state.isMapReady || onClick) return;
    
    const timeoutId = setTimeout(() => {
      if (locations.length > 0) {
        addLocationMarkers(
          locations,
          refs,
          showUserLocation,
          state.userLocation,
          onLocationClick,
          showHoverCard,
          hideHoverCard,
          showMobileBottomSheet
        );
      }
    }, isMobile ? 200 : 100); // Больше задержка на мобильных

    return () => clearTimeout(timeoutId);
  }, [locations, state.isMapReady, onClick, showUserLocation, state.userLocation, onLocationClick, showHoverCard, hideHoverCard, showMobileBottomSheet, isMobile]);

  // Обновление пользователя
  useEffect(() => {
    if (!mapInstanceRef.current || !state.isMapReady) return;
    if (showUserLocation && state.userLocation) {
      updateUserLocationMarker(state.userLocation, refs);
    }
  }, [state.userLocation, state.isMapReady, showUserLocation]);

  const onSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSearch(
      state.searchQuery,
      refs,
      (coords) => updateState({ coordinates: coords }),
      onClick
    );
  }, [state.searchQuery, onClick, updateState]);

  const onCoordinatesSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCoordinatesSubmit(state.coordinates, refs, onClick);
  }, [state.coordinates, onClick]);

  const onMyLocationClick = useCallback(() => {
    handleMyLocationClick(
      refs,
      (location) => updateState({ userLocation: location }),
      onClick
    );
  }, [onClick, updateState]);

  if (!withSearch) {
    return (
      <div className="relative w-full h-full" style={{ height }}>
        <div 
          ref={mapRef} 
          className="absolute inset-0 w-full h-full map-container"
        />
        <button
          onClick={onMyLocationClick}
          className="absolute bottom-3 right-3 p-2.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
          title="Маё месцазнаходжанне"
        >
          <Crosshair size={18} className="text-primary" />
        </button>

        {/* Desktop Hover Preview Card - только на десктопе */}
        {!isMobile && state.hoverCard && (
          <MapHoverCard
            hoverCard={state.hoverCard}
            onLocationClick={onLocationClick}
            onHide={hideHoverCard}
          />
        )}

        {/* Mobile Bottom Sheet - только на мобильных */}
        {isMobile && state.mobileBottomSheet && (
          <MobileBottomSheet
            location={state.mobileBottomSheet}
            isAnimating={state.isSheetAnimating}
            onLocationClick={onLocationClick}
            onHide={hideMobileBottomSheet}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
      <SearchControls
        searchQuery={state.searchQuery}
        coordinates={state.coordinates}
        onSearchQueryChange={(query) => updateState({ searchQuery: query })}
        onCoordinatesChange={(coords) => updateState({ coordinates: coords })}
        onSearch={onSearch}
        onCoordinatesSubmit={onCoordinatesSubmit}
        onMyLocationClick={onMyLocationClick}
      />

      <div className="relative flex-1">
        <div ref={mapRef} className="absolute inset-0 map-container" />
        
        {/* Desktop Hover Preview Card - только на десктопе */}
        {!isMobile && state.hoverCard && (
          <MapHoverCard
            hoverCard={state.hoverCard}
            onLocationClick={onLocationClick}
            onHide={hideHoverCard}
          />
        )}

        {/* Mobile Bottom Sheet - только на мобильных */}
        {isMobile && state.mobileBottomSheet && (
          <MobileBottomSheet
            location={state.mobileBottomSheet}
            isAnimating={state.isSheetAnimating}
            onLocationClick={onLocationClick}
            onHide={hideMobileBottomSheet}
          />
        )}
      </div>
    </div>
  );
};